// src/ui/SoundPanel.jsx — putting a song under a clip, the way the phone does.
//
// 🟢 THE BROWSER'S HALF OF THE PHONE'S SOUNDS DRAWER. Same shelves, same
//    catalogue, same licence rule, same credit, same mix arithmetic — all from
//    services/soundLibrary, generated from the phone's TypeScript. What is
//    different is only what a pointer and a keyboard make different.
//
// ══ THE SHAPE, WHICH IS TIKTOK'S ══════════════════════════════════════════
//   search at the top · shelves as chips · rows with cover, title, artist and
//   length · a tap PREVIEWS the song under the clip at once · the chosen row
//   wears "Use" · a window slid along the song picks where it starts · two
//   sliders mix the clip's own sound against the song, each with a mute.
//
// 🟥 THE CREDIT IS ON THE ROW. Jamendo's API terms want the artist named,
//    Jamendo named as the provider, and the track linked. Every row says all
//    three, and the post the song ends up on says them again (MusicLine).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  SOUND_CATEGORIES, jamendoTracksUrl, usableSounds, fmtDuration, maxStartMs, clampStartMs,
  effectiveVolumes, musicPositionFor, MIX_DEFAULT, PROVIDER_LABEL,
} from '../services/soundLibrary';
import { Search, Pause, Play, X } from './icons';

export const SOUND_TEXT = {
  title: 'Sounds',
  search: 'Search songs, artists…',
  use: 'Use',
  remove: 'Remove sound',
  startsAt: 'Song starts at',
  original: 'Original sound',
  added: 'Added sound',
  mute: 'Mute',
  unmute: 'Unmute',
  none: 'No sounds found for that. Try another shelf.',
  loading: 'Loading sounds…',
  credit: (artist, license) => `${artist} · via ${PROVIDER_LABEL}${license ? ` · ${license}` : ''}`,
};

/**
 * The panel. `sound` is `{ track, startMs, mix }` on the edit, or null.
 * @param videoRef the preview <video>, so the song can be slaved to it
 * @param dur the clip's length in ms (for the start window)
 */
export default function SoundPanel({ sound, onChange, videoRef, dur }) {
  const [q, setQ] = useState('');
  const [shelf, setShelf] = useState('trending');
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [musicDur, setMusicDur] = useState(0);
  const audioRef = useRef(null);
  const timer = useRef(null);

  const track = sound?.track || null;
  const mix = { ...MIX_DEFAULT, ...(sound?.mix || {}) };
  const startMs = sound?.startMs || 0;

  /* ── the catalogue ─────────────────────────────────────────────────────── */
  const load = async (search, tag) => {
    setErr(''); setRows(null);
    try {
      const res = await fetch(jamendoTracksUrl({ search, tag, limit: 60 }));
      const j = await res.json();
      if (j?.headers?.status === 'failed') throw new Error(j.headers.error_message || 'rejected');
      /* 🟥 ONLY WHAT MAY GO UNDER A POST — CC0 and CC BY. The query already
         asked Jamendo to leave NC, ND and SA out; this checks what came back. */
      setRows(usableSounds(j?.results));
    } catch (e) {
      setRows([]); setErr('Could not reach the sound library.');
    }
  };
  useEffect(() => { load('', SOUND_CATEGORIES.find((c) => c.key === shelf)?.tag ?? null); }, [shelf]); // eslint-disable-line react-hooks/exhaustive-deps
  const onSearch = (v) => {
    setQ(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => load(v, SOUND_CATEGORIES.find((c) => c.key === shelf)?.tag ?? null), 450);
  };

  /* ── the preview: the song, slaved to the clip, at the author's mix ─────── */
  const ev = effectiveVolumes(mix, 1);
  useEffect(() => {
    const v = videoRef?.current;
    const m = audioRef.current;
    if (!v) return undefined;
    /* the clip's own sound is heard only while there is a song to mix it against
       — the selection click is the gesture that lets the browser unmute */
    if (track) { v.muted = ev.original <= 0; v.volume = Math.max(0, Math.min(1, ev.original)); }
    else { v.muted = true; }
    if (!m) return undefined;
    m.volume = Math.max(0, Math.min(1, ev.added));
    m.muted = ev.added <= 0;
    const sync = () => {
      if (!m || !v) return;
      const d = Number.isFinite(m.duration) ? m.duration * 1000 : 0;
      if (d && d !== musicDur) setMusicDur(d);
      const want = musicPositionFor(v.currentTime * 1000, d, startMs) / 1000;
      if (v.paused || v.ended) { if (!m.paused) m.pause(); return; }
      if (m.paused) { try { m.currentTime = want; } catch {} m.play().catch(() => {}); }
      else if (Math.abs(m.currentTime - want) > 0.3) { try { m.currentTime = want; } catch {} }
    };
    v.addEventListener('timeupdate', sync);
    v.addEventListener('play', sync);
    v.addEventListener('pause', sync);
    m.addEventListener('loadedmetadata', sync);
    sync();
    return () => {
      v.removeEventListener('timeupdate', sync);
      v.removeEventListener('play', sync);
      v.removeEventListener('pause', sync);
      m.removeEventListener('loadedmetadata', sync);
    };
  }, [track?.url, startMs, ev.original, ev.added, videoRef, musicDur]);

  /* 🟢 THE MIX IS HEARD ON THIS TAB ONLY. Leaving it mutes the preview again —
     the song element goes with the panel, and a clip playing its own sound
     without the song would be a mix nobody chose. */
  useEffect(() => () => { const v = videoRef?.current; if (v) v.muted = true; }, [videoRef]);

  const setSound = (next) => onChange(next);
  const pick = (row) => {
    if (track && track.id === row.id) { setSound(null); return; }   // tap again = remove
    setSound({ track: row, startMs: 0, mix: { ...mix, isAddedSoundMuted: false } });
  };
  const maxStart = maxStartMs(musicDur, dur);

  return (
    <div className="mx-sound">
      <label className="mx-sound-search">
        <Search width={14} height={14} />
        <input value={q} placeholder={SOUND_TEXT.search} onChange={(e) => onSearch(e.target.value)} aria-label="Search sounds" />
        {q && <button type="button" className="mx-sound-clear" onClick={() => onSearch('')} aria-label="Clear"><X width={12} height={12} /></button>}
      </label>

      <div className="mx-sound-shelves" role="tablist">
        {SOUND_CATEGORIES.map((c) => (
          <button key={c.key} type="button" role="tab" aria-selected={shelf === c.key} className={shelf === c.key ? 'on' : ''} onClick={() => { setQ(''); setShelf(c.key); }}>{c.label}</button>
        ))}
      </div>

      {track && (
        <div className="mx-sound-mix">
          {/* 🎵 where the song starts — the window slid along the track */}
          <label className="mx-range">
            <span>{SOUND_TEXT.startsAt}</span>
            <input
              type="range" min="0" max={Math.max(0, Math.round(maxStart))} step="100" value={Math.round(Math.min(startMs, maxStart))}
              disabled={maxStart <= 0} aria-label={SOUND_TEXT.startsAt}
              onChange={(e) => {
                const next = clampStartMs(Number(e.target.value), musicDur, dur);
                setSound({ ...sound, startMs: next });
                const m = audioRef.current; if (m) { try { m.currentTime = next / 1000; } catch {} }
              }}
            />
            <b>{fmtDuration(startMs / 1000)}</b>
          </label>
          <label className="mx-range">
            <span>{SOUND_TEXT.original}</span>
            <input type="range" min="0" max="100" value={mix.originalVolume} aria-label={SOUND_TEXT.original}
              onChange={(e) => setSound({ ...sound, mix: { ...mix, originalVolume: Number(e.target.value) } })} />
            <button type="button" className={`mx-mute ${mix.isOriginalMuted ? 'on' : ''}`} aria-pressed={mix.isOriginalMuted}
              title={mix.isOriginalMuted ? SOUND_TEXT.unmute : SOUND_TEXT.mute}
              onClick={() => setSound({ ...sound, mix: { ...mix, isOriginalMuted: !mix.isOriginalMuted } })}>
              {mix.isOriginalMuted ? '🔇' : '🎤'}
            </button>
          </label>
          <label className="mx-range">
            <span>{SOUND_TEXT.added}</span>
            <input type="range" min="0" max="100" value={mix.addedSoundVolume} aria-label={SOUND_TEXT.added}
              onChange={(e) => setSound({ ...sound, mix: { ...mix, addedSoundVolume: Number(e.target.value) } })} />
            <button type="button" className={`mx-mute ${mix.isAddedSoundMuted ? 'on' : ''}`} aria-pressed={mix.isAddedSoundMuted}
              title={mix.isAddedSoundMuted ? SOUND_TEXT.unmute : SOUND_TEXT.mute}
              onClick={() => setSound({ ...sound, mix: { ...mix, isAddedSoundMuted: !mix.isAddedSoundMuted } })}>
              {mix.isAddedSoundMuted ? '🔇' : '🎵'}
            </button>
          </label>
          <button type="button" className="mx-plain sm" onClick={() => setSound(null)}>{SOUND_TEXT.remove}</button>
          <audio ref={audioRef} src={track.url} preload="auto" />
        </div>
      )}

      {err && <p className="mx-hint">{err}</p>}
      {rows === null ? <p className="mx-hint">{SOUND_TEXT.loading}</p>
        : rows.length === 0 ? <p className="mx-hint">{SOUND_TEXT.none}</p> : (
          <ul className="mx-sound-list">
            {rows.map((r) => {
              const on = track?.id === r.id;
              return (
                <li key={r.id} className={on ? 'on' : ''}>
                  <button type="button" className="mx-sound-row" onClick={() => pick(r)} aria-pressed={on}>
                    <span className="mx-sound-cover">
                      {r.cover ? <img src={r.cover} alt="" /> : null}
                      <i>{on && !mix.isAddedSoundMuted ? <Pause width={16} height={16} /> : <Play width={16} height={16} />}</i>
                    </span>
                    <span className="mx-sound-meta">
                      <b>{r.title}</b>
                      {/* the credit the terms require, with the real length */}
                      <em>{SOUND_TEXT.credit(r.artist, r.license?.label)} · {fmtDuration(r.duration)}</em>
                    </span>
                    {r.page && (
                      <a className="mx-sound-link" href={r.page} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={`On ${PROVIDER_LABEL}`}>↗</a>
                    )}
                  </button>
                  {on && <span className="mx-sound-use">{SOUND_TEXT.use} ✓</span>}
                </li>
              );
            })}
          </ul>
        )}
    </div>
  );
}
